// file: `react/src/components/UserContext.tsx`:

import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { expressURL, getAuthHeaders } from "../utils/networkUtils";
import { AUTH_TOKEN_CHANGED_EVENT, clearAuthToken, hasSession, markCookieSession } from '../utils/authToken.ts';

export type User = {
  id: string | number;
  email?: string;
  shortName: string;
  fullName: string;
  emoji: string;
  colorNum: string;
  role: string;
  userType?: string;
  cryptoEnabled?: boolean;
  cryptoPublicKey?: JsonWebKey | null;
  crypto?: {
    enabled: boolean;
    wrap?: string;
    publicKeyJwk?: JsonWebKey;
    wrappedPrivateKey?: { iv: string; ciphertext: string };
    sss?: { n: number; k: number; holderLabels: string[] };
  };
  schoolClaims?: {
    laboratory?: boolean;
    municipalityId?: string;
    schoolId?: string;
    schoolName?: string;
    classId?: string;
    yearGroup?: number | null;
    roles?: string[];
    householdId?: string;
  };
};

type UserContextType = {
  activeUser: User | null;
  setActiveUser: (user: User | null) => void;
  users: User[];
  fetchUser: () => Promise<void>;
  fetchAllUsers: () => Promise<void>;
};

const defaultUserContext: UserContextType = {
  activeUser: null,
  users: [],
  setActiveUser: () => { },
  fetchUser: async () => { },
  fetchAllUsers: async () => { },
};

const UserContext = createContext<UserContextType>(defaultUserContext);

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [activeUser, setActiveUser] = useState<User | null>(null);
  const usersFetchedForId = useRef<string | null>(null);

  const fetchAllUsers = useCallback(async (): Promise<void> => {
    if (!hasSession()) return;
    try {
      const response = await axios.get(`${expressURL()}/api/get-users`, getAuthHeaders());
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  const clearSession = useCallback(() => {
    setActiveUser(null);
    setUsers([]);
    usersFetchedForId.current = null;
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${expressURL()}/auth/me`, getAuthHeaders());

      if (response.data) {
        markCookieSession(true);
        setActiveUser(response.data);
        const id = String(response.data.id);
        if (usersFetchedForId.current !== id) {
          usersFetchedForId.current = id;
          await fetchAllUsers();
        }
      } else {
        console.error('UserContext.tsx: fetchUser no data in response');
      }
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : null;
      if (status === 401 || status === 403) {
        if (hasSession()) clearAuthToken();
        clearSession();
        return;
      }
      console.error('UserContext.tsx: Failed to fetch user:', error);
    }
  }, [clearSession, fetchAllUsers]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  useEffect(() => {
    const onTokenChanged = () => {
      if (!hasSession()) clearSession();
    };
    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, onTokenChanged);
    return () => window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, onTokenChanged);
  }, [clearSession]);

  useEffect(() => {
    if (!activeUser) {
      usersFetchedForId.current = null;
      return;
    }
    const id = String(activeUser.id);
    if (!hasSession()) return;
    if (usersFetchedForId.current === id) return;
    usersFetchedForId.current = id;
    fetchAllUsers();
  }, [activeUser, fetchAllUsers]);

  return (
    <UserContext.Provider value={{ activeUser, setActiveUser, users, fetchUser, fetchAllUsers }}>
      {children}
    </UserContext.Provider>
  );
};

export { UserContext };
